import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getAssessmentByClientID,
  createAssessment,
  deleteAssessmentID,
  updateAssessment,
} from "../../../../../../api/services/observations";
import CustomInput from "../../../../../CustomForm/CustomInput";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const getCurrentTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getInitialFormData = () => ({
  incident_date: getTodayDate(),
  time: getCurrentTime(),
  incident_type: "",
  description: "",
  injuries: "",
  handover: false,
  manager: false,
});

const Falls = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState(getInitialFormData());
  const [editFormData, setEditFormData] = useState(null);
  const [editId, setEditId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rowID, setRowID] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const profile = useUserProfile();

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      if (isEditing) {
        setEditFormData((prev) => ({ ...prev, [name]: value }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    },
    [isEditing]
  );

  const handleCheckBox = useCallback(
    (checked, name) => {
      if (isEditing) {
        setEditFormData((prev) => ({ ...prev, [name]: checked }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
    },
    [isEditing]
  );

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData());
    setIsEditing(false);
    setEditFormData(null);
    setEditId(null);
  }, []);

  const { data: assessments, isLoading } = useQuery(
    [QK.falls, clientID],
    () => getAssessmentByClientID(clientID, "falls"),
    { enabled: !!clientID }
  );

  const createMutation = useMutation(createAssessment, {
    onSuccess: () => {
      enqueueSnackbar("Fall incident recorded successfully.", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.falls, clientID]);
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to record fall incident.",
        { variant: "error" }
      );
    },
  });

  const updateMutation = useMutation(
    ({ id, data }) => updateAssessment(id, data),
    {
      onSuccess: () => {
        enqueueSnackbar("Fall record updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.falls, clientID]);
        resetForm();
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to update fall record",
          { variant: "error" }
        );
      },
    }
  );

  const deleteMutation = useMutation(deleteAssessmentID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.falls, clientID]);
      enqueueSnackbar("Fall record deleted successfully", {
        variant: "success",
      });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete fall record", { variant: "error" });
    },
  });

  const validateForm = useCallback((data) => {
    if (!data.incident_date) return false;
    if (!data.time) return false;
    if (!data.incident_type) return false;
    if (!data.description) return false;
    return true;
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (isEditing) {
        if (validateForm(editFormData)) {
          updateMutation.mutate({ id: editId, data: editFormData });
        }
      } else {
        if (validateForm(formData)) {
          createMutation.mutate({
            ...formData,
            client: clientID,
            carer: profile?.id,
            assessment_type: "falls",
          });
        }
      }
    },
    [
      formData,
      editFormData,
      isEditing,
      editId,
      clientID,
      profile,
      createMutation,
      updateMutation,
      validateForm,
    ]
  );

  const handleEdit = useCallback((id, rowData) => {
    setEditId(id);
    setEditFormData({
      incident_date: rowData.incident_date || "",
      time: rowData.time || "",
      incident_type: rowData.incident_type || "",
      description: rowData.description || "",
      injuries: rowData.injuries || "",
      handover: rowData.handover || false,
      manager: rowData.manager || false,
    });
    setIsEditing(true);
    document
      .getElementById("falls-form")
      ?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleDelete = useCallback((ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (rowID) deleteMutation.mutate(rowID);
  }, [rowID, deleteMutation]);

  const handleCancelEdit = useCallback(() => resetForm(), [resetForm]);

  const headers = useMemo(
    () => [
      { key: "incident_date", label: "Incident Date" },
      { key: "time", label: "Time" },
      { key: "incident_type", label: "Incident Type" },
      { key: "description", label: "Description" },
      { key: "injuries", label: "Injuries Sustained" },
      { key: "manager", label: "Manager" },
      { key: "handover", label: "Handover" },
    ],
    []
  );

  const renderCell = useCallback((item, header) => {
    if (header.key === "incident_date") {
      return item.incident_date
        ? new Date(item.incident_date).toLocaleDateString()
        : "-";
    }
    if (header.key === "time") {
      if (!item.time) return "-";
      try {
        const [h, m] = item.time.split(":");
        const date = new Date();
        date.setHours(Number(h), Number(m));
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return item.time || "-";
      }
    }
    if (header.key === "incident_type") {
      return (
        <Chip
          label={item.incident_type || "N/A"}
          size="small"
          sx={{
            height: 22,
            fontSize: "0.68rem",
            fontWeight: 600,
            bgcolor: item.incident_type?.toLowerCase().includes("fall")
              ? "#FEE2E2"
              : "#E0E7FF",
            color: item.incident_type?.toLowerCase().includes("fall")
              ? "#DC2626"
              : "#4338CA",
            borderRadius: "6px",
          }}
        />
      );
    }
    if (header.key === "description") {
      return (
        <span
          style={{
            display: "block",
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.description || "-"}
        </span>
      );
    }
    if (header.key === "injuries") {
      return (
        <span
          style={{
            display: "block",
            maxWidth: 150,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.injuries || "None reported"}
        </span>
      );
    }
    if (header.key === "manager") {
      return item.manager ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#D1FAE5",
            color: "#039855",
            borderRadius: "6px",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
            borderRadius: "6px",
          }}
        />
      );
    }
    if (header.key === "handover") {
      return item.handover ? (
        <Chip
          label="Yes"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#D1FAE5",
            color: "#039855",
            borderRadius: "6px",
          }}
        />
      ) : (
        <Chip
          label="No"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#FEE2E2",
            color: "#DC2626",
            borderRadius: "6px",
          }}
        />
      );
    }
    return item[header.key] || "-";
  }, []);

  const currentFormData = isEditing ? editFormData : formData;
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Box>
      <Card
        sx={{
          borderRadius: "14px",
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          mb: 3,
          border: "1px solid #EEF1F5",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #FAFBFC 0%, #F4F7FB 100%)",
            borderBottom: "1px solid #EEF1F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.error.main,
                  0.14
                )} 0%, ${alpha(theme.palette.error.main, 0.04)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              }}
            >
              <WarningIcon
                sx={{ color: theme.palette.error.main, fontSize: 20 }}
              />
            </Box>
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: "#1A1A2E", lineHeight: 1.2 }}
                >
                  {isEditing ? "Edit Fall Incident" : "Record Fall Incident"}
                </Typography>
                <Chip
                  label={isEditing ? "Editing" : "New"}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    bgcolor: isEditing ? "#FEF3C7" : "#D1FAE5",
                    color: isEditing ? "#D97706" : "#039855",
                    borderRadius: "6px",
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "#7A8699", fontSize: "0.68rem" }}
              >
                {isEditing
                  ? "Update the selected incident record"
                  : "Log a new fall incident with details"}
              </Typography>
            </Box>
          </Box>
          {isEditing && (
            <Button
              size="small"
              onClick={handleCancelEdit}
              sx={{
                textTransform: "none",
                color: "#6B7280",
                fontSize: "0.75rem",
                fontWeight: 500,
                borderRadius: "8px",
                px: 1.5,
                py: 0.5,
                border: "1px solid #E4E7EC",
                bgcolor: "#FFFFFF",
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            >
              Cancel Edit
            </Button>
          )}
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit} id="falls-form">
            <Grid container spacing={2.25}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <CustomDateField
                  name="incident_date"
                  label="Incident Date *"
                  value={currentFormData?.incident_date || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <CustomTimePicker
                  label="Time *"
                  name="time"
                  value={currentFormData?.time || ""}
                  onChange={handleChange}
                  required
                  size="small"
                  fontSize="0.8rem"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <CustomInput
                  label="Incident Type *"
                  name="incident_type"
                  value={currentFormData?.incident_type || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="e.g., Slip, Trip, Fall"
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <CustomTextArea
                  label="Description of the incident *"
                  name="description"
                  value={currentFormData?.description || ""}
                  onChange={handleChange}
                  rows={3}
                  required
                  fullWidth
                  placeholder="Provide detailed description of the incident..."
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <CustomTextArea
                  label="Injuries sustained"
                  name="injuries"
                  value={currentFormData?.injuries || ""}
                  onChange={handleChange}
                  rows={3}
                  fullWidth
                  placeholder="Describe any injuries sustained..."
                />
              </Grid>

              <Grid item size={12}>
                <Divider sx={{ my: 0.5, borderStyle: "dashed" }} />
              </Grid>

              <Grid item size={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    flexWrap: "wrap",
                    alignItems: "center",
                    px: 2,
                    borderRadius: "10px",
                    border: "1px solid #EEF1F5",
                    bgcolor: "#FAFBFC",
                  }}
                >
                  <CustomCheckbox
                    label="Share with Manager"
                    name="manager"
                    checked={currentFormData?.manager || false}
                    onClick={handleCheckBox}
                  />
                  <CustomCheckbox
                    label="Handover"
                    name="handover"
                    checked={currentFormData?.handover || false}
                    onClick={handleCheckBox}
                  />
                </Box>
              </Grid>

              <Grid item size={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                    pt: 1.5,
                    mt: 0.5,
                    borderTop: "1px dashed #E5E9F0",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    startIcon={<ClearIcon fontSize="small" />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      borderColor: "#D0D5DD",
                      color: "#344054",
                      px: 3,
                      py: 0.9,
                      fontSize: "0.82rem",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#98A2B3",
                      },
                    }}
                  >
                    {isEditing ? "Cancel" : "Clear"}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SaveIcon fontSize="small" />
                      )
                    }
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      bgcolor: isEditing ? "#D97706" : "#DC2626",
                      px: 3.5,
                      py: 0.9,
                      fontSize: "0.82rem",
                      boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                      "&:hover": {
                        bgcolor: isEditing ? "#B45309" : "#B91C1C",
                        boxShadow: isEditing
                          ? "0 4px 12px rgba(217, 119, 6, 0.25)"
                          : "0 4px 12px rgba(220, 38, 38, 0.25)",
                      },
                    }}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : isEditing
                      ? "Update Incident"
                      : "Save Incident"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: "14px",
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          border: "1px solid #EEF1F5",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #FAFBFC 0%, #F4F7FB 100%)",
            borderBottom: "1px solid #EEF1F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "#1A1A2E" }}
            >
              Fall Incident Records
            </Typography>
            <Chip
              label={assessments?.length || 0}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 600,
                bgcolor: "#F3F4F6",
                color: "#475467",
                borderRadius: "6px",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={assessments || []}
            headers={headers}
            onUpdate={handleEdit}
            onDelete={handleDelete}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
            size="small"
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this fall incident record?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default Falls;